import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";
import { scrollToHash } from "../config/site";

const LINKS = [
  { label: "Home", hash: "#top" },
  { label: "Franchise", mobileLabel: "Franchise Opportunity", path: "/franchise" },
  { label: "Our Story", hash: "#story" },
  { label: "Products", hash: "#products" },
  { label: "Why TMV", hash: "#why" },
  { label: "Dealer", hash: "#dealer" },
  { label: "Contact", hash: "#contact" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const go = (item) => {
    setOpen(false);
    if (item.path) {
      navigate(item.path);
      if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
      window.scrollTo(0, 0);
    } else if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToHash(item.hash), 350);
    } else {
      scrollToHash(item.hash);
    }
  };

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled
            ? "border-b border-white/10 bg-espresso/80 py-2 backdrop-blur-xl"
            : "border-b border-transparent bg-transparent py-5"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 md:px-10">
          <button
            data-testid="navbar-logo-button"
            onClick={() => go("#top")}
            className="focus:outline-none focus:ring-1 focus:ring-gold"
            aria-label="TMV Coffee home"
          >
            <Logo compact={scrolled} />
          </button>

          <nav className="hidden items-center gap-7 lg:flex">
            {LINKS.map((l) => (
              <button
                key={l.label}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => go(l)}
                className="group relative text-[13px] font-medium uppercase tracking-[0.18em] text-cream/70 transition-colors duration-300 hover:text-cream focus:outline-none focus:ring-1 focus:ring-gold"
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-gold transition-all duration-500 group-hover:w-full" />
              </button>
            ))}
          </nav>

          <button
            data-testid="mobile-menu-open-button"
            onClick={() => setOpen(true)}
            className="rounded-full border border-cream/20 p-2.5 text-cream transition-colors duration-300 hover:border-gold hover:text-gold lg:hidden"
            aria-label="Open menu"
          >
            <Menu size={18} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu-overlay"
            className="fixed inset-0 z-[60] flex flex-col bg-espresso/95 backdrop-blur-xl lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
          >
            <div className="flex items-center justify-between px-5 py-5">
              <Logo compact />
              <button
                data-testid="mobile-menu-close-button"
                onClick={() => setOpen(false)}
                className="rounded-full border border-cream/20 p-2.5 text-cream hover:border-gold hover:text-gold"
                aria-label="Close menu"
              >
                <X size={18} />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.label}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => go(l)}
                  className="border-b border-cream/10 py-4 text-left font-display text-3xl text-cream transition-colors duration-300 hover:text-gold"
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  {l.mobileLabel || l.label}
                </motion.button>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
